import useCatalogEnabled from "catalog/hooks/useCatalogEnabled";
import useCatalogSearch from "catalog/hooks/useCatalogSearch";
import usePinnedEntities from "catalog/hooks/usePinnedEntities";
import pluralize from "pluralize";
import useUser from "~/contexts/user";
import useGlobalFileBrowserEnabled from "~/hooks/useGlobalFileBrowserEnabled";
import useUserPermission from "~/hooks/useUserPermission";
import { TableEntity, VariableEntity } from "~/types/interfaces/catalogResponse";

const numFormatter = Intl.NumberFormat("en", { notation: "compact" });
const { VITE_DATA_DASHBOARD_URL, VITE_SIGMA_REPORT_IDS } = import.meta.env;

export interface DataBrowserResource {
  href: string;
  label: string;
  description: string;
  icon: string;
}

const useDataBrowserResources = () => {
  const user = useUser();
  const showFileBrowser = useGlobalFileBrowserEnabled();
  const canViewOverviewReports = useUserPermission("view:overview-reports");
  const catalogEnabled = useCatalogEnabled();
  const {
    data: tables,
    isLoading: loadingTables,
    error: tableError,
  } = useCatalogSearch<TableEntity>("table");
  const {
    data: columns,
    isLoading: loadingColumns,
    error: columnError,
  } = useCatalogSearch<VariableEntity>("column");
  const { total: totalPinned } = usePinnedEntities();

  const loading = !user || loadingTables || loadingColumns;
  const error = Boolean(tableError || columnError);
  const tablesTotal = Number(tables?.hits?.total?.value);
  const columnsTotal = Number(columns?.hits?.total?.value);

  const resources: DataBrowserResource[] = [];

  if (showFileBrowser) {
    resources.push({
      href: "/data-browser/files",
      label: "Files",
      description: "A central place to organize and manage your data.",
      icon: "files",
    });
  }

  if (canViewOverviewReports && (VITE_DATA_DASHBOARD_URL || VITE_SIGMA_REPORT_IDS)) {
    resources.push({
      href: "/data-browser/dashboards",
      label: "Dashboards",
      description: "Curated reports to explore our organizations data.",
      icon: "dashboards",
    });
  }

  if (catalogEnabled) {
    if (tablesTotal) {
      resources.push({
        href: "/data-browser/tables",
        label: `${numFormatter.format(tablesTotal)} ${pluralize("Table", tablesTotal)}`,
        description: "Repository to unify and analyze your research data.",
        icon: "tables",
      });
    }

    if (columnsTotal) {
      resources.push({
        href: "/data-browser/columns",
        label: `${numFormatter.format(columnsTotal)} ${pluralize("Column", columnsTotal)}`,
        description: "Define the attributes and fields in your data tables.",
        icon: "columns",
      });
    }

    // Always show Pins when catalog is enabled to promote discoverability
    resources.push({
      href: "/data-browser/pins",
      label: `${numFormatter.format(totalPinned)} ${pluralize("Pin", totalPinned)}`,
      description: "Save important tables and columns for quick access.",
      icon: "pins",
    });
  }

  return {
    resources,
    loading,
    error,
  };
};

export default useDataBrowserResources;
